import { RouterProvider } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import './index.css';
import { useStore } from './store/useStore';
import { api, readAuthToken, subscribeAuthFailure, TOKEN_STORAGE_KEY, writeAuthToken } from './api/client';
import { createAuthBootstrap, normalizeUser } from './authBootstrap';
import { createSeemeRouter } from './router';

function LoginView({ onLogin }) {
  const [username, setUsername] = useState('admin');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const result = await api.login({ username, password });
      writeAuthToken(result.token || result.tokenValue || null);
      onLogin(result);
    } catch (loginError) {
      setError(loginError.message || '登录失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-shell">
      <form className="login-panel" onSubmit={submit}>
        <div>
          <div className="login-kicker">Seeme CRM</div>
          <h1>登录工作台</h1>
          <p>使用 ERP 账号登录，登录后可进入会话、客户、任务和渠道配置。</p>
        </div>
        <label>
          <span>账号</span>
          <input value={username} onChange={event => setUsername(event.target.value)} autoComplete="username" />
        </label>
        <label>
          <span>密码</span>
          <input value={password} onChange={event => setPassword(event.target.value)} type="password" autoComplete="current-password" />
        </label>
        {error ? <div className="login-error">{error}</div> : null}
        <button className="login-submit" type="submit" disabled={submitting}>
          {submitting ? '登录中...' : '登录'}
        </button>
      </form>
    </div>
  );
}

export default function App() {
  const loadConversations = useStore(state => state.loadConversations);
  const loadContacts = useStore(state => state.loadContacts);
  const loadTasks = useStore(state => state.loadTasks);
  const loadAnalytics = useStore(state => state.loadAnalytics);
  const connectWebSocket = useStore(state => state.connectWebSocket);
  const disconnectWebSocket = useStore(state => state.disconnectWebSocket);
  const updateUserSettings = useStore(state => state.updateUserSettings);
  const [authChecked, setAuthChecked] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [authRevision, setAuthRevision] = useState(0);

  const loadApplicationData = useCallback(() => {
    loadConversations();
    loadContacts();
    loadTasks();
    loadAnalytics();
    connectWebSocket();
  }, [loadConversations, loadContacts, loadTasks, loadAnalytics, connectWebSocket]);

  const clearSession = useCallback(() => {
    writeAuthToken(null);
    disconnectWebSocket();
    setAuthenticated(false);
    setAuthChecked(true);
  }, [disconnectWebSocket]);

  useEffect(() => {
    setAuthChecked(false);
    createAuthBootstrap({
      api,
      readToken: readAuthToken,
      clearToken: () => writeAuthToken(null),
      loadApplicationData,
      setAuthenticated,
      setAuthChecked,
      updateUserSettings,
    })();
  }, [authRevision, loadApplicationData, updateUserSettings]);

  useEffect(() => subscribeAuthFailure(() => clearSession()), [clearSession]);

  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key !== TOKEN_STORAGE_KEY) {
        return;
      }
      if (!event.newValue) {
        clearSession();
        return;
      }
      setAuthRevision(revision => revision + 1);
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [clearSession]);

  const handleLogin = (user) => {
    updateUserSettings(normalizeUser(user));
    setAuthenticated(true);
    setAuthChecked(true);
    loadApplicationData();
  };

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } catch (error) {
      // Token cleanup still happens locally.
    }
    writeAuthToken(null);
    disconnectWebSocket();
    setAuthenticated(false);
  }, [disconnectWebSocket]);

  const router = useMemo(() => createSeemeRouter({ onLogout: logout }), [logout]);

  if (!authChecked) {
    return <div className="login-shell"><div className="login-panel">正在检查登录状态...</div></div>;
  }

  if (!authenticated) {
    return <LoginView onLogin={handleLogin} />;
  }

  return (
    <RouterProvider router={router} />
  );
}
